import { useMutation, useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { startCase } from 'lodash-es';

import { statisticApi } from '@/api';
import type { PeriodUnit } from '@/component/Statistic/type';
import { QUERY_KEY_CONST } from '@/constant';
import { deepCopy } from '@/package';
import { useAuthStore } from '@/store';
import type { Project, StatisticApi, Task, User } from '@/type';

export const useSyncStatisticQuery = () => {
  return useMutation({
    mutationFn: ({ taskID, frameSeq }: StatisticApi.SyncStatistic.Request) =>
      statisticApi.syncStatistic({ taskID, frameSeq }),
  });
};

const convertPeriod = (period: PeriodUnit) => {
  return startCase(period) as StatisticApi.GetMetric.Request['period'];
};

const convertBetween = (between: [Dayjs, Dayjs]) => {
  return [
    dayjs(between[0])
      .startOf('day')
      .set('hour', 0)
      .set('minute', 0)
      .set('second', 0)
      .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
    dayjs(between[1])
      .endOf('day')
      .set('hour', 23)
      .set('minute', 59)
      .set('second', 59)
      .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
  ] as [string, string];
};

/** useMetricQuery */
export const useMetricQuery = (params: StatisticApi.GetMetric.Request) => {
  return useQuery({
    queryKey: QUERY_KEY_CONST.STATISTIC_KEYS.getMetric(params),
    queryFn: () =>
      statisticApi.getUserMetric(params).then(res => res.data.resultData),
  });
};

export const useTaskPeriodMetricQuery = ({
  projectID,
  taskID,
  period,
  between,
  workerID,
}: Pick<Required<StatisticApi.GetMetric.Request>, 'projectID' | 'taskID'> & {
  period: PeriodUnit;
  workerID?: User['id'];
  between: [Dayjs, Dayjs];
}) => {
  const convertedPeriod = convertPeriod(period);
  const convertedBetween = convertBetween(between);
  const params = {
    projectID,
    taskID,
    period: convertedPeriod,
    between: convertedBetween,
    workerID,
  };

  Object.entries(params).forEach(([key, value]) => {
    if (value == null) delete params[key as keyof typeof params];
  });

  return useMetricQuery(params);
};

export const useTaskTotalMetricQuery = ({
  projectID,
  taskID,
}: Pick<Required<StatisticApi.GetMetric.Request>, 'projectID' | 'taskID'>) => {
  const params = {
    projectID,
    taskID,
    period: 'Total' as const,
  };

  return useMetricQuery(params);
};

export const useProjectPeriodMetricQuery = ({
  projectID,
  period,
  between,
}: Pick<Required<StatisticApi.GetMetric.Request>, 'projectID'> & {
  between: [Dayjs, Dayjs];
  period: PeriodUnit;
}) => {
  const convertedPeriod = convertPeriod(period);
  const convertedBetween = convertBetween(between);
  const params = {
    projectID,
    period: convertedPeriod,
    between: convertedBetween,
  };

  return useMetricQuery(params);
};

export const useProjectTotalMetricQuery = ({
  projectID,
}: Pick<Required<StatisticApi.GetMetric.Request>, 'projectID'>) => {
  const params = {
    projectID,
    period: 'Total' as const,
  };

  return useMetricQuery(params);
};

export const useMyWorkProjectPeriodMetricQuery = ({
  period,
  between,
}: {
  period: PeriodUnit;
  between: [Dayjs, Dayjs];
}) => {
  const convertedPeriod = convertPeriod(period);
  const convertedBetween = convertBetween(between);
  const params = {
    workerID: useAuthStore.getState().user.id,
    period: convertedPeriod,
    between: convertedBetween,
  };

  return useMetricQuery(params);
};

export const useMyWorkProjectTotalMetricQuery = () => {
  const params = {
    workerID: useAuthStore.getState().user.id,
    period: 'Total' as const,
  };

  return useMetricQuery(params);
};

export const useMyWorkTaskPeriodMetricQuery = ({
  projectID,
  taskID,
  period,
  between,
}: Pick<Required<StatisticApi.GetMetric.Request>, 'projectID' | 'taskID'> & {
  period: PeriodUnit;
  between: [Dayjs, Dayjs];
}) => {
  const convertedPeriod = convertPeriod(period);
  const convertedBetween = convertBetween(between);
  const params = {
    projectID,
    taskID,
    period: convertedPeriod,
    between: convertedBetween,
    workerID: useAuthStore.getState().user.id,
  };

  return useMetricQuery(params);
};

export const useMyWorkTaskTotalMetricQuery = ({
  projectID,
  taskID,
}: Pick<Required<StatisticApi.GetMetric.Request>, 'projectID' | 'taskID'>) => {
  const params = {
    projectID,
    taskID,
    period: 'Total' as const,
    workerID: useAuthStore.getState().user.id,
  };

  return useMetricQuery(params);
};

/** useObjectQuery Start */
export const useObjectQuery = (params: StatisticApi.GetObject.Request) => {
  const convertedParams = Object.entries(params).reduce((acc, [key, value]) => {
    if (value == null) delete acc[key as keyof typeof acc];

    return acc;
  }, deepCopy(params));

  return useQuery({
    queryKey: QUERY_KEY_CONST.STATISTIC_KEYS.getObject(convertedParams),
    queryFn: () =>
      statisticApi.getObject(convertedParams).then(res => res.data.resultData),
  });
};

export const useTaskObjectQuery = (
  projectID: Project['id'],
  taskID: Task['id'],
  workerID?: User['id'],
  calSum: boolean = false,
) => {
  return useObjectQuery({ projectID, taskID, workerID, calSum });
};

export const useProjectObjectQuery = (
  projectID: Project['id'],
  calSum: boolean = false,
) => {
  return useObjectQuery({ projectID, calSum });
};

export const useMyWorkTotalObjectQuery = (calSum: boolean = false) => {
  return useObjectQuery({ workerID: useAuthStore.getState().user.id, calSum });
};

export const useMyWorkTaskObjectQuery = (
  projectID: Project['id'],
  taskID: Task['id'],
  calSum: boolean = false,
) => {
  return useObjectQuery({
    projectID,
    taskID,
    workerID: useAuthStore.getState().user.id,
    calSum,
  });
};

/** useObjectQuery End */

// TODO: rename 필요
/** useStateQuery Start */
export const useStateQuery = (params: StatisticApi.GetState.Request) => {
  const convertedParams = Object.entries(params).reduce((acc, [key, value]) => {
    if (value == null) delete acc[key as keyof typeof acc];
    return acc;
  }, deepCopy(params));

  return useQuery({
    queryKey: QUERY_KEY_CONST.STATISTIC_KEYS.getState(convertedParams),
    queryFn: () =>
      statisticApi.getState(convertedParams).then(res => res.data.resultData),
  });
};

export const useProjectFrameStateQuery = (projectID: Project['id']) => {
  return useStateQuery({ pTarget: 'frame', projectID });
};

export const useTaskFrameStateQuery = (
  projectID: Project['id'],
  taskID: Task['id'],
  workerID?: User['id'],
) => {
  return useStateQuery({
    pTarget: 'frame',
    projectID,
    taskID,
    workerID,
  });
};

export const useMyTaskFrameStateQuery = (
  projectID: Project['id'],
  taskID: Task['id'],
) => {
  return useStateQuery({
    pTarget: 'frame',
    projectID,
    taskID,
    workerID: useAuthStore.getState().user.id,
  });
};

export const useMyTotalFrameStateQuery = () => {
  return useStateQuery({
    pTarget: 'frame',
    workerID: useAuthStore.getState().user.id,
  });
};

export const useMyTotalTaskStateQuery = () => {
  return useStateQuery({
    pTarget: 'task',
    workerID: useAuthStore.getState().user.id,
  });
};

/** useStateQuery End */

/** useGetWorkTimeQuery Start */
export const useGetWorkTimeQuery = (
  params: StatisticApi.GetWorktimeForAdmin.Request,
) => {
  const convertedParams = Object.entries(params).reduce((acc, [key, value]) => {
    if (value == null) delete acc[key as keyof typeof acc];
    return acc;
  }, deepCopy(params));

  return useQuery({
    queryKey: QUERY_KEY_CONST.STATISTIC_KEYS.getWorktime(convertedParams),
    queryFn: () =>
      statisticApi
        .getWorktimeForAdmin(convertedParams)
        .then(res => res.data.resultData),
  });
};

export const useGetProjectWorktimeQuery = (
  param: SelectiveRequired<
    StatisticApi.GetWorktimeForAdmin.Request,
    'projectID'
  >,
) => {
  return useGetWorkTimeQuery(param);
};

export const useGetProjectWorktimeGroupByDateQuery = (
  param: Omit<
    SelectiveRequired<StatisticApi.GetWorktimeForAdmin.Request, 'projectID'>,
    'groupBy'
  >,
) => {
  return useGetWorkTimeQuery({ ...param, groupBy: 'date' });
};

export const useGetTaskWorktimeQuery = (
  param: SelectiveRequired<
    StatisticApi.GetWorktimeForAdmin.Request,
    'projectID' | 'taskID'
  >,
) => {
  return useGetWorkTimeQuery(param);
};

export const useGetTaskWorkerWorktimeQuery = (
  param: SelectiveRequired<
    StatisticApi.GetWorktimeForAdmin.Request,
    'projectID' | 'taskID' | 'workerID'
  >,
) => {
  return useGetWorkTimeQuery(param);
};

export const useGetMyTaskWorktimeQuery = (
  param?: Omit<StatisticApi.GetWorktimeForAdmin.Request, 'workerID'>,
) => {
  return useQuery({
    queryKey: QUERY_KEY_CONST.STATISTIC_KEYS.getWorktime(param),
    queryFn: () =>
      statisticApi
        .getWorktimeForWorker({
          ...param,
          workerID: useAuthStore.getState().user.id,
        })
        .then(res => res.data.resultData),
  });
};
/** useGetWorkTimeQuery End */
